import dayjs from 'dayjs';
import { toastConfig } from 'config';
import { toast } from 'react-toastify';
import EditIcon from 'assets/icons/edit';
import { useSelector } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import { stateUpdater } from '../utils/helperFunc';
import { useEffect, useRef, useState } from 'react';
import { NumericFormat } from 'react-number-format';
import { withValueCap } from 'pages/globals/global';
import constantFormValues from '../utils/formInputNames';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGetQueryV3Mutation } from 'store/features/api/graphSlice';
import { Col, DatePicker, Form, Input, Row, Select, Button } from 'antd';
import { useIdentityBulkMutation } from 'store/features/process/processApi';
import RestorePageOutlinedIcon from '@mui/icons-material/RestorePageOutlined';
import ComptrollerConfirmModal from 'pages/comptroller/ComptrollerConfirmModal';
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import SectionCard from 'components/section/SectionCard';
import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableFooter, Typography } from '@mui/material';

const LeaseDetailBasicInformation = ({ screen, viewData, vendorList, dataFullyLoaded, showFooterActions = true }) => {
	const form = Form.useFormInstance();
	const LeaseEndDate = dayjs().add(1, 'month');

	const currentLeaseTerm = Form.useWatch(constantFormValues?.leaseTerm?.key, form);
	const currentPaymentFrequency = Form.useWatch(constantFormValues?.paymentFrequency?.key, form);

	useEffect(() => {
		const currentPaymentFrequency = form.getFieldValue(constantFormValues?.paymentFrequency?.key);

		if (currentPaymentFrequency === 'Quarterly') {
			form.setFieldValue('modifiedLeaseTerm', Math.floor(form.getFieldValue('leaseTerm') / 3));
		} else {
			form.setFieldValue('modifiedLeaseTerm', form.getFieldValue('leaseTerm'));
		}
	}, [currentLeaseTerm, currentPaymentFrequency]);

	const grossRentKey = useRef('');
	const isUpdatingRef = useRef(false);
	const advanceAmountKey = useRef('');
	const updateReleaseTimeoutRef = useRef(null);

	const grossRentList = Form.useWatch('grossRentList', form);
	const advanceAmountList = Form.useWatch('advanceAmountList', form);

	useEffect(() => {
		if (!Array.isArray(grossRentList) || !Array.isArray(advanceAmountList) || grossRentList.length === 0 || advanceAmountList.length === 0)
			return;

		const newGrossRentKey = grossRentList.map((item) => `${item.from}-${item.to}`).join('|');
		const newAdvanceAmountKey = advanceAmountList.map((item) => `${item.from}-${item.to}`).join('|');

		if (newGrossRentKey === grossRentKey.current && newAdvanceAmountKey === advanceAmountKey.current) return;
		if (isUpdatingRef.current) return;

		isUpdatingRef.current = true;

		try {
			grossRentKey.current = newGrossRentKey;
			advanceAmountKey.current = newAdvanceAmountKey;
			const maxGrossRentTerm = Math.max(...grossRentList.map((item) => item.to ?? 0));
			const maxAdvanceAmountTerm = Math.max(...advanceAmountList.map((item) => item.to ?? 0));
			const maxTerm = Math.max(maxGrossRentTerm, maxAdvanceAmountTerm);

			const currentLeaseTerm = form.getFieldValue('leaseTerm');

			if (currentLeaseTerm !== maxTerm) {
				form.setFieldValue('leaseTerm', maxTerm);
			}

			const leaseStartDate = form.getFieldValue('leaseStartDate');

			if (leaseStartDate && maxTerm) {
				const leaseEndDate = leaseStartDate.add(maxTerm, 'month').subtract(1, 'day');
				const currentLeaseEndDate = form.getFieldValue('leaseEndDate');
				if (!currentLeaseEndDate || !leaseEndDate.isSame(currentLeaseEndDate)) {
					form.setFieldValue('leaseEndDate', leaseEndDate);
				}
			}
		} finally {
			if (updateReleaseTimeoutRef.current) {
				clearTimeout(updateReleaseTimeoutRef.current);
			}

			updateReleaseTimeoutRef.current = setTimeout(() => {
				isUpdatingRef.current = false;
			}, 0);
		}
	}, [grossRentList, advanceAmountList]);

	useEffect(() => {
		return () => {
			if (updateReleaseTimeoutRef.current) {
				clearTimeout(updateReleaseTimeoutRef.current);
			}
		};
	}, []);

	const [totalAmount, setTotalAmount] = useState(0);

	const formAdvanceAmount = Form.useWatch(constantFormValues?.advanceAmount?.key, form);

	useEffect(() => {
		if (formAdvanceAmount !== undefined && formAdvanceAmount !== null) {
			if (formAdvanceAmount !== totalAmount) {
				setTotalAmount(formAdvanceAmount);
			}
		}
	}, [formAdvanceAmount]);

	const calculateTotal = () => {
		try {
			const values = form.getFieldValue('advanceAmountList');

			if (values && Array.isArray(values)) {
				const currentRowTotals = values.map((item) => {
					if (!item) return 0;

					const fromValue = parseInt(item.from);
					const toValue = parseInt(item.to);

					let amount = 0;

					if (item.amount !== undefined && item.amount !== null && item.amount !== '') {
						const amountStr = String(item.amount).replace(/,/g, '');
						amount = parseFloat(amountStr);
					}

					if (isNaN(fromValue) || isNaN(toValue) || isNaN(amount) || toValue < fromValue) {
						return 0;
					}

					return (toValue - fromValue + 1) * amount;
				});

				const sum = currentRowTotals.reduce((total, rowTotal) => total + (rowTotal || 0), 0);

				setTotalAmount(sum);
			}
		} catch (error) {
			console.error('Error calculating total:', error);
		}
	};

	useEffect(() => {
		if (screen === 'VIEW' && dataFullyLoaded) {
			const timeoutId = setTimeout(() => {
				const currentValues = form.getFieldValue('advanceAmountList');

				if (currentValues && currentValues.length > 0) {
					calculateTotal();
				}
			}, 700);

			return () => clearTimeout(timeoutId);
		}
	}, [screen, dataFullyLoaded]);

	const { state } = useLocation();

	const [formState, setFormState] = useState({
		submitPayload: null,
		showFormSubmitModal: false,
		showActiveDeactiveModal: false,
		modalData: {
			head: state?.processName ?? '',
			label: 'Do you want to proceed?',
		},
	});

	const updateFormState = stateUpdater(setFormState);

	const [draftSubmit, { isSuccess: isDraftSubmitSuccess }] = useIdentityBulkMutation();

	const handleSubmitClick = () => {
		const payloadData = [viewData?._id];

		const formattedPayload = {
			suffixCode: '',
			_id: payloadData,
			operation: 'submit-draft-bulk',
			processCode: state?.processCode,
		};

		updateFormState('showFormSubmitModal', true);
		updateFormState('modalData.head', 'Confirmation');
		updateFormState('modalData.label', 'Do you want to submit this lease record?');

		updateFormState('submitPayload', formattedPayload);
	};

	const handleModalSubmit = async () => {
		await draftSubmit(formState?.submitPayload);
	};

	const setShowFormSubmitModal = (value) => {
		updateFormState('showFormSubmitModal', value);
	};

	const [getLeaseTableData, { data: leaseControlDataV3 }] = useGetQueryV3Mutation();
	const [IdentityBulk, { isSuccess: isCaseSuccess, isError: isCaseError }] = useIdentityBulkMutation();

	const [isDraft, setIsDraft] = useState(false);

	const theme = useTheme();

	const navigate = useNavigate();

	const { creatorOrganizationId } = useSelector((state) => state.profile.profileData);
	const companyCode = creatorOrganizationId;

	const isLeaseLifeCycleOver = () => {
		const currentDate = dayjs();
		const leaseEndDate = dayjs(form.getFieldValue('leaseEndDate'));

		return currentDate.isAfter(leaseEndDate);
	};

	const [areAllRecordsRecorded, setAreAllRecordsRecorded] = useState(false);
	const [showClosedModal, setShowClosedModal] = useState(false);
	const [modalData, setModalData] = useState({
		head: state?.processName ?? '',
		label: 'Do you want to proceed?',
	});

	const [activeDeactiveSubmitData, setActiveDeactiveSubmitData] = useState(null);

	const handleSingleLeaseDeactivate = (status) => {
		if (status === 'Closed') {
			setModalData({
				head: state?.processName ?? 'Lease Record',
				action: status,
				label: <p>Do you want to close this lease record?</p>,
			});
		}
		if (status === 'Reopened') {
			setModalData({
				head: state?.processName ?? 'Lease Record',
				action: status,
				label: <p>Do you want to re-open this lease record?</p>,
			});
		}

		const singleLeasePayload = [{ _id: state._id }];
		setActiveDeactiveSubmitData(singleLeasePayload);
		setShowClosedModal(true);
	};

	const modalSubmitHandler = () => {
		if (modalData?.action === 'Closed') {
			const payload = {
				operation: 'activate-bulk',
				activationStatus: 'Closed',
				processCode: state?.processCode,
				_id: activeDeactiveSubmitData.map((item) => item._id),
			};

			IdentityBulk(payload);
		} else if (modalData?.action === 'Reopened') {
			const payload = {
				operation: 'activate-bulk',
				activationStatus: 'Reopened',
				processCode: state?.processCode,
				_id: activeDeactiveSubmitData.map((item) => item._id),
			};

			IdentityBulk(payload);
		}
		setShowClosedModal(false);
	};

	useEffect(() => {
		if (isCaseSuccess) {
			toast.success('Success! Lease status updated successfully', toastConfig);

			navigate(-1);
		} else if (isCaseError) {
			toast.error('Failed! Status update not completed', toastConfig);
			setShowClosedModal(false);
		}
	}, [isCaseSuccess, isCaseError]);

	const queryV3Loader = async () => {
		const version = String(viewData?.data?.version);

		if (version && version !== 'null' && version !== 'undefined' && companyCode) {
			await getLeaseTableData({
				companyCode,
				name: 'leaseSchedulesOnVersion-v9',
				fieldValues: [companyCode, state?._id, version],
				fieldCodes: ['companyCode', 'identityRecordIdString', 'version'],
			});
		}
	};

	useEffect(() => {
		if (!showFooterActions) return;

		if (isDraftSubmitSuccess) {
			navigate(-1);
		}
	}, [isDraftSubmitSuccess, navigate, showFooterActions]);

	useEffect(() => {
		if (!showFooterActions) return;

		setIsDraft(viewData?.activationStatus?.toLowerCase() === 'draft');
	}, [showFooterActions, viewData?.activationStatus]);

	useEffect(() => {
		if (!showFooterActions) return;

		queryV3Loader();
	}, [screen, dataFullyLoaded, viewData?._id, viewData?.version, showFooterActions]);

	useEffect(() => {
		if (!showFooterActions) return;

		if (Array.isArray(leaseControlDataV3?.results)) {
			const isAllRecordsRecorded = leaseControlDataV3?.results?.every((item) => item?.processStatus === 'Recorded');

			setAreAllRecordsRecorded(isAllRecordsRecorded);
		}
	}, [leaseControlDataV3?.results, showFooterActions]);

	const sectionTitleSx = {
		color: '#344054',
		padding: '0 0 12px 0',
		fontWeight: 600,
		fontSize: '18px',
		borderBottom: '1px solid #eaeef4',
	};

	const detailSectionCardSx = {
		padding: '16px',
		borderRadius: '10px',
		border: '1px solid #e6ebf2',
	};

	return (
		<Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
			<SectionCard sx={detailSectionCardSx}>
				<Typography variant="h4" sx={sectionTitleSx}>
					Basic Information
				</Typography>

				<Row gutter={[16, 0]} style={{ marginTop: '16px' }}>
					<Form.Item noStyle name="tabIndex" />
					<Form.Item noStyle name="endOfFinancialYear" />
					<Form.Item noStyle name={constantFormValues?.version?.key} />
					<Form.Item noStyle name={constantFormValues?.leaseTerm?.key} />
					<Form.Item noStyle name={constantFormValues?.lessorName?.key} />
					<Form.Item noStyle name={constantFormValues?.paymentModalitylabel?.key} />

					<Col xs={24} md={8}>
						<Form.Item name={constantFormValues?.LeaseId?.key} label={constantFormValues?.LeaseId?.label}>
							<Input disabled placeholder="To be auto generated" />
						</Form.Item>
					</Col>

					<Col xs={24} md={8}>
						<Form.Item name={constantFormValues?.leaseTitle?.key} label={constantFormValues?.leaseTitle?.label}>
							<Input disabled={screen === 'VIEW'} placeholder="Write here" />
						</Form.Item>
					</Col>

					<Col xs={24} md={8}>
						<Form.Item name={constantFormValues?.lessorID?.key} label={constantFormValues?.lessorID?.label}>
							<Select
								showSearch
								disabled={true}
								filterOption={true}
								optionFilterProp="children"
								placeholder="Select from the list" style={{width: '100%'}}
							>
								{vendorList?.map(({ label, value }) => (
									<Select.Option key={value} value={value}>
										{label}
									</Select.Option>
								))}
							</Select>
						</Form.Item>
					</Col>

					<Col xs={24} md={8}>
						<Form.Item name={constantFormValues?.leaseStartDate?.key} label={constantFormValues?.leaseStartDate?.label}>
							<DatePicker disabled={true} format="YYYY-MM-DD" style={{width: '100%'}} />
						</Form.Item>
					</Col>

					<Col xs={24} md={8}>
						<Form.Item name={constantFormValues?.leaseEndDate?.key} label={constantFormValues?.leaseEndDate?.label}>
							<DatePicker disabled value={LeaseEndDate} style={{width: '100%'}} />
						</Form.Item>
					</Col>

					<Col xs={24} md={8}>
						<Form.Item name="modifiedLeaseTerm" label={constantFormValues?.leaseTerm?.label}>
							<Input type="number" disabled placeholder="Write here" />
						</Form.Item>
					</Col>

					<Form.Item noStyle name={constantFormValues?.paymentModality?.key} />

					<Col xs={24} md={8}>
						<Form.Item name={constantFormValues?.leaseType?.key} label={constantFormValues?.leaseType?.label}>
							<Select
								showSearch
								disabled={true}
								filterOption={true}
								optionFilterProp="children"
								placeholder="Select from the list" style={{width: '100%'}}
							>
								{constantFormValues?.leaseType?.options?.map(({ key, value, label }) => (
									<Select.Option key={key} value={value}>
										{label}
									</Select.Option>
								))}
							</Select>
						</Form.Item>
					</Col>

					<Form.Item
						shouldUpdate={(previousValue, currentValue) =>
							previousValue?.[constantFormValues?.leaseType?.key] !== currentValue?.[constantFormValues?.leaseType?.key]
						}
						noStyle
					>
						{({ getFieldValue }) => {
							if (getFieldValue(constantFormValues?.leaseType?.key) === 'Finance Lease') {
								return (
									<Col xs={24} md={8}>
										<Form.Item
											name={constantFormValues?.initialDiscountRate?.key}
											label={constantFormValues?.initialDiscountRate?.label}
										>
											<Input disabled={true} placeholder="Write here" />
										</Form.Item>
									</Col>
								);
							}
							return <Form.Item name={constantFormValues?.initialDiscountRate?.key} noStyle />;
						}}
					</Form.Item>

					<Col xs={24} md={8}>
						<Form.Item name={constantFormValues?.creationDate?.key} label={constantFormValues?.creationDate?.label}>
							<DatePicker type="date" disabled={true} format="YYYY-MM-DD" style={{width: '100%'}} />
						</Form.Item>
					</Col>

					<Col xs={24} md={8}>
						<Form.Item name={constantFormValues?.lastModifiedDate?.key} label={constantFormValues?.lastModifiedDate?.label}>
							<DatePicker type="date" disabled={true} format="YYYY-MM-DD" style={{width: '100%'}} />
						</Form.Item>
					</Col>
				</Row>
			</SectionCard>

			<Row gutter={[24, 24]}>
				<Col xs={24} md={12}>
					<SectionCard sx={{ ...detailSectionCardSx, height: '100%' }}>
						<Typography variant="h4" sx={{ ...sectionTitleSx, marginBottom: '16px' }}>
							Lease Payment Schedule
						</Typography>

						<Form.List name={'grossRentList'}>
							{(fields) => (
								<>
									<TableContainer
										className="gross-rent-table"
										sx={{
											'& table, & th, & td': {
												border: '1px solid #e0e0e0',
												borderCollapse: 'collapse',
											},
											'& th, & td': {
												p: '5px',
											},
											'& td .ant-form-item': {
												marginBottom: '0px',
											},
											'& th:nth-of-type(1), & td:nth-of-type(1)': { width: '20%' },
											'& th:nth-of-type(2), & td:nth-of-type(2)': { width: '20%' },
											'& th:nth-of-type(3), & td:nth-of-type(3)': { width: '20%' },
											'& th:nth-of-type(4), & td:nth-of-type(4)': { width: '20%' },
											'& th:nth-of-type(5), & td:nth-of-type(5)': { width: '20%' },
											'& table': { width: '100%' },
										}}
									>
										<Table aria-label="Gross Rent table">
										<TableHead>
											<TableRow>
												<TableCell sx={{ textAlign: 'center', fontSize: '13px', backgroundColor: '#f6f5f5' }}>
													From Month
												</TableCell>

												<TableCell sx={{ textAlign: 'center', fontSize: '13px', backgroundColor: '#f6f5f5' }}>
													To Month
												</TableCell>

												<TableCell sx={{ textAlign: 'center', fontSize: '13px', backgroundColor: '#f6f5f5' }}>
													Amount
												</TableCell>
											</TableRow>
										</TableHead>
										<TableBody>
											{fields.length > 0 ? (
												fields.map((field, index) => (
													<TableRow key={field.key}>
														<TableCell>
															<Form.Item name={[field.name, 'from']}>
																<Input
																	disabled={true} style={{width: '100%',
																		// padding: '8px',
																		textAlign: 'center',
																		backgroundColor: '#f5f5f5'}}
																/>
															</Form.Item>
														</TableCell>

														<TableCell>
															<Form.Item name={[field.name, 'to']}>
																<Input
																	type="number"
																	disabled={true}
																	placeholder="Enter To value" style={{width: '100%',
																		// padding: '8px',
																		textAlign: 'center'}}
																/>
															</Form.Item>
														</TableCell>

														<TableCell>
															<Form.Item name={[field.name, 'amount']}>
																<NumericFormat
																	disabled={true}
																	decimalScale={2}
																	fixedDecimalScale
																	placeholder="0.00"
																	customInput={Input}
																	thousandSeparator=","
																	isAllowed={withValueCap}
																/>
															</Form.Item>
														</TableCell>
													</TableRow>
												))
											) : (
												<TableRow>
													<TableCell colSpan={5} style={{ textAlign: 'center' }}>
														No data. Click plus icon in action column to add new entries.
													</TableCell>
												</TableRow>
											)}
										</TableBody>
									</Table>
								</TableContainer>

									<Form.Item name="grossRents" noStyle />
								</>
							)}
						</Form.List>

						<Form.Item noStyle name="identityId" />
						<Form.Item noStyle name="grossRentListSet" />
						<Form.Item noStyle name="effectiveDateHistory" />
						<Form.Item noStyle name="modificationSequence" />

						<Row gutter={[16, 0]} style={{ marginTop: '16px' }}>
							<Col xs={24} md={12}>
								<Form.Item name={constantFormValues?.paymentFrequency?.key} label={constantFormValues?.paymentFrequency?.label}>
									<Select
										showSearch
										disabled={true}
										filterOption={true}
										optionFilterProp="children"
										placeholder="Select from the list" style={{width: '100%'}}
									>
										{constantFormValues?.paymentFrequency?.options?.map(({ key, value, label }) => (
											<Select.Option key={key} value={value}>
												{label}
											</Select.Option>
										))}
									</Select>
								</Form.Item>
							</Col>

							<Col xs={24} md={12}>
								<Form.Item name={constantFormValues?.paymentModality?.key} label={constantFormValues?.paymentModality?.label}>
									<Select
										showSearch
										disabled={true}
										filterOption={true}
										optionFilterProp="children"
										placeholder="Select from the list" style={{width: '100%'}}
									>
										{constantFormValues?.paymentModality?.options?.map(({ key, value, label }) => (
											<Select.Option key={key} value={value}>
												{label}
											</Select.Option>
										))}
									</Select>
								</Form.Item>
							</Col>
						</Row>
					</SectionCard>
				</Col>

				<Col xs={24} md={12}>
					<SectionCard sx={{ ...detailSectionCardSx, height: '100%' }}>
						<Typography variant="h4" sx={{ ...sectionTitleSx, marginBottom: '16px' }}>
							Advance Payment & Adjustment
						</Typography>

						<Form.List name={'advanceAmountList'}>
						{(fields) => {
							return (
								<>
									<TableContainer
										className="gross-rent-table"
										sx={{
											'& table, & th, & td': {
												border: '1px solid #e0e0e0',
												borderCollapse: 'collapse',
											},
											'& th, & td': {
												p: '5px',
											},
											'& td .ant-form-item': {
												marginBottom: '0px',
											},

											'& th:nth-of-type(1), & td:nth-of-type(1)': { width: '20%' },
											'& th:nth-of-type(2), & td:nth-of-type(2)': { width: '20%' },
											'& th:nth-of-type(3), & td:nth-of-type(3)': { width: '20%' },
											'& th:nth-of-type(4), & td:nth-of-type(4)': { width: '20%' },
											'& th:nth-of-type(5), & td:nth-of-type(5)': { width: '20%' },
											'& table': { width: '100%' },
										}}
									>
										<Table aria-label="Gross Rent table">
											<TableHead>
												<TableRow>
													<TableCell sx={{ fontSize: '13px', backgroundColor: '#f6f5f5', textAlign: 'center' }}>
														From Month
													</TableCell>

													<TableCell sx={{ fontSize: '13px', backgroundColor: '#f6f5f5', textAlign: 'center' }}>
														To Month
													</TableCell>

													<TableCell sx={{ fontSize: '13px', backgroundColor: '#f6f5f5', textAlign: 'center' }}>
														Monthly Adjustment
													</TableCell>
												</TableRow>
											</TableHead>

											<TableBody>
												{fields.length > 0 ? (
													fields.map((field, index) => (
														<TableRow key={field.key}>
															<TableCell>
																<Form.Item name={[field.name, 'from']}>
																	<Input
																		disabled={true} style={{width: '100%',
																			textAlign: 'center',
																			backgroundColor: '#f5f5f5'}}
																	/>
																</Form.Item>
															</TableCell>

															<TableCell>
																<Form.Item name={[field.name, 'to']}>
																	<Input
																		type="number"
																		disabled={true}
																		placeholder="Enter To value" style={{width: '100%',
																			textAlign: 'center'}}
																	/>
																</Form.Item>
															</TableCell>

															<TableCell>
																<Form.Item name={[field.name, 'amount']}>
																	<NumericFormat
																		disabled={true}
																		decimalScale={2}
																		fixedDecimalScale
																		placeholder="0.00"
																		customInput={Input}
																		thousandSeparator=","
																		isAllowed={withValueCap}
																	/>
																</Form.Item>
															</TableCell>
														</TableRow>
													))
												) : (
													<TableRow>
														<TableCell colSpan={5} style={{ textAlign: 'center' }}>
															No data. Click the plus icon to add new entries.
														</TableCell>
													</TableRow>
												)}
											</TableBody>

											<TableFooter>
												<TableRow sx={{ height: 24 }}>
													<TableCell
														colSpan={2}
														align="right"
														sx={{ fontWeight: 'bold', fontSize: '13px', backgroundColor: '#f6f5f5', pr: '14px' }}
													>
														Advance Payment
													</TableCell>

													<TableCell style={{ fontWeight: 'bold', textAlign: 'right' }}>
														<NumericFormat
															disabled={true}
															readOnly={true}
															decimalScale={2}
															fixedDecimalScale
															displayType="input"
															customInput={Input}
															thousandSeparator=","
															value={totalAmount || 0} style={{fontWeight: 'bold'}}
														/>
													</TableCell>
												</TableRow>
											</TableFooter>
										</Table>
									</TableContainer>

									<Form.Item name="advanceAmounts" noStyle />
								</>
							);
						}}
						</Form.List>

						<Form.Item name="advanceAmountListSet" noStyle />
					</SectionCard>
				</Col>
			</Row>

			<SectionCard sx={detailSectionCardSx}>
				<Typography
					variant="h4"
					sx={{
						...sectionTitleSx,
						marginBottom: '16px',
					}}
				>
					Other Payments & Incentive
				</Typography>

				<Row gutter={[16, 0]}>
						<Col xs={24} md={6}>
							<Form.Item name={constantFormValues?.upfrontLeasePayment?.key} label={constantFormValues?.upfrontLeasePayment?.label}>
								<NumericFormat
									disabled={true}
									decimalScale={2}
									placeholder="0.00"
									fixedDecimalScale
									customInput={Input}
									thousandSeparator=","
									isAllowed={withValueCap}
								/>
							</Form.Item>
						</Col>

						<Col xs={24} md={6}>
							<Form.Item name={constantFormValues?.initialDirectCost?.key} label={constantFormValues?.initialDirectCost?.label}>
								<NumericFormat
									disabled={true}
									decimalScale={2}
									placeholder="0.00"
									fixedDecimalScale
									customInput={Input}
									thousandSeparator=","
									isAllowed={withValueCap}
								/>
							</Form.Item>
						</Col>

						<Col xs={24} md={6}>
							<Form.Item name={constantFormValues?.securityDeposit?.key} label={constantFormValues?.securityDeposit?.label}>
								<NumericFormat
									disabled={true}
									decimalScale={2}
									placeholder="0.00"
									fixedDecimalScale
									customInput={Input}
									thousandSeparator=","
									isAllowed={withValueCap}
								/>
							</Form.Item>
						</Col>

						<Col xs={24} md={6}>
							<Form.Item name={constantFormValues?.cashIncentiveAmount?.key} label={constantFormValues?.cashIncentiveAmount?.label}>
								<NumericFormat
									disabled={true}
									decimalScale={2}
									placeholder="0.00"
									fixedDecimalScale
									customInput={Input}
									thousandSeparator=","
									isAllowed={withValueCap}
								/>
							</Form.Item>
						</Col>
					</Row>

				<Form.Item noStyle name="grossRents" />
				<Form.Item noStyle name="advanceAmounts" />
				<Form.Item noStyle name={constantFormValues?.advanceAmount?.key} />
				<Form.Item noStyle name={constantFormValues?.inputChangeMonth?.key} />
			</SectionCard>

			<SectionCard sx={detailSectionCardSx}>
				<Typography
					variant="h4"
					sx={{
						...sectionTitleSx,
						marginBottom: '16px',
					}}
				>
					IFRS - 16 Specific Fields
				</Typography>

				<Row gutter={[16, 16]}>
					<Col xs={24}>
						<Form.List name="changes">
						{(fields) => (
							<TableContainer
								sx={{
									width: '100%',
									display: 'block',
									overflowX: 'auto',
									position: 'relative',

									'& table, & th, & td': {
										border: '1px solid #e0e0e0',
										borderCollapse: 'collapse',
									},
									'& th, & td': {
										p: '5px',
									},
									'& td .ant-form-item': {
										marginBottom: '0px',
									},

									'& td:not(:last-child)': { borderRight: '1px solid #e0e0e0' },
									'& tr:not(:last-child)': { borderBottom: '1px solid #e0e0e0' },
									'& th': { borderBottom: '1px solid #e0e0e0' },
								}}
							>
								<Table
									sx={{
										'& .MuiTableBody-root .MuiTableCell-root': { px: '5px', py: '5px' },
										tableLayout: 'fixed',
									}}
								>
									<TableHead>
										<TableRow>
											<Form.Item noStyle shouldUpdate>
												{({ getFieldValue }) => {
													const leaseType = getFieldValue('leaseType');

													return (
														<>
															{constantFormValues?.changes?.orderedRowHeader.map((key) => {
																if (key === 'inputChangeDiscountRate' && leaseType === 'Operating Lease') return null;

																return (
																	<TableCell
																		key={constantFormValues?.changes?.changeList[key].key}
																		sx={{ fontSize: '13px', backgroundColor: '#f6f5f5' }}
																		align="center"
																	>
																		{constantFormValues?.changes?.changeList[key].label}
																	</TableCell>
																);
															})}
														</>
													);
												}}
											</Form.Item>
										</TableRow>
									</TableHead>

									<TableBody>
										{fields?.map((field, index) => {
											return (
												<TableRow key={index}>
													<TableCell style={{ position: 'relative' }}>
														<Form.Item
															style={{ margin: 0 }}
															name={[
																field.name,
																constantFormValues?.changes?.changeList['inputChangeEffectiveDate'].key,
															]}
														>
															<DatePicker
																disabled={true}
																format="YYYY-MM-DD"
																className="antd-datePicker" style={{width: '100%'}}
															/>
														</Form.Item>
													</TableCell>

													<TableCell>
														<Form.Item
															style={{ margin: 0 }}
															name={[field.name, constantFormValues?.changes?.changeList['inputChangeDescription'].key]}
														>
															<Select
																disabled
																showSearch
																filterOption={true} style={{textAlign: 'center'}}
																options={constantFormValues?.changes?.changeList['inputChangeDescription'].options}
															/>
														</Form.Item>
													</TableCell>

													<TableCell>
														<Form.Item
															style={{ margin: 0 }}
															name={[field.name, constantFormValues?.changes?.changeList['inputChangeLeaseTerm'].key]}
														>
															<Input
																disabled
																type="number" style={{marginBottom: 0,
																	textAlign: 'center',
																	color:
																		index !== 0
																			? form.getFieldValue(['changes', index, 'inputChangeLeaseTerm']) ===
																				form.getFieldValue(['changes', index - 1, 'inputChangeLeaseTerm'])
																				? 'black'
																				: 'blue'
																			: 'black'}}
															/>
														</Form.Item>
													</TableCell>

													<Form.Item name={[field.name, 'inputChangeDiscountRate']} noStyle />

													<Form.Item noStyle shouldUpdate={(prev, current) => prev.leaseType !== current.leaseType}>
														{({ getFieldValue }) =>
															getFieldValue('leaseType') === 'Finance Lease' ? (
																<TableCell align="center">
																	<Form.Item
																		style={{ margin: 0 }}
																		name={[
																			field.name,
																			constantFormValues?.changes?.changeList['inputChangeDiscountRate'].key,
																		]}
																	>
																		<NumericFormat
																			disabled={true}
																			decimalScale={2}
																			placeholder="0.00"
																			fixedDecimalScale
																			customInput={Input}
																			thousandSeparator=","
																			isAllowed={withValueCap} style={{textAlign: 'center',
																				color:
																					index !== 0
																						? form.getFieldValue([
																								'changes',
																								index,
																								'inputChangeDiscountRate',
																							]) ===
																							form.getFieldValue([
																								'changes',
																								index - 1,
																								'inputChangeDiscountRate',
																							])
																							? 'black'
																							: 'blue'
																						: 'black'}}
																		/>
																	</Form.Item>
																</TableCell>
															) : null
														}
													</Form.Item>

													<TableCell>
														<Form.Item
															style={{ margin: 0 }}
															name={[field.name, constantFormValues?.changes?.changeList['paymentFrequency'].key]}
														>
															<Select
																disabled
																showSearch
																filterOption={true} style={{textAlign: 'center'}}
																options={constantFormValues?.changes?.changeList['paymentFrequency'].options}
															/>
														</Form.Item>
													</TableCell>

													<TableCell>
														<Form.Item
															style={{ margin: 0 }}
															name={[field.name, constantFormValues?.changes?.changeList['paymentTime'].key]}
														>
															<Select
																disabled
																showSearch
																filterOption={true} style={{textAlign: 'center'}}
																options={constantFormValues?.changes?.changeList['paymentTime'].options}
															/>
														</Form.Item>
													</TableCell>
												</TableRow>
											);
										})}
									</TableBody>
								</Table>
							</TableContainer>
						)}
						</Form.List>
					</Col>
				</Row>
			</SectionCard>

			{showFooterActions && (
				<Box
					sx={{
						gap: '4px',
						mt: '16px',
						display: 'flex',
						flexDirection: 'column',
						justifyContent: 'space-between',
					}}
				>
					<Box
						sx={{
							gap: '4px',
							display: 'flex',
							flexDirection: 'row',
							justifyContent: 'space-between',
						}}
					>
						<Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
							{viewData && (
								<>
									{!isDraft ? (
										viewData?.activationStatus !== 'W-I-P' &&
										viewData?.activationStatus !== 'Closed' && (
											<Button
												style={{
													margin: '1px',
													display: 'flex',
													fontSize: '13px',
													marginLeft: '6px',
													fontWeight: '500',
													borderRadius: '4px',
													alignItems: 'center',
												}}
												icon={<EditIcon width="18px" height="18px" color="#00914f" />}
												onClick={(e) => {
													e.stopPropagation();
													if (navigate && state) {
														navigate('/static/lease-record-management-v9/modify-new-version', {
															state: state,
														});
													}
												}}
											>
												Modify
											</Button>
										)
									) : (
										<Button
											type="primary"
											onClick={handleSubmitClick}
											style={{
												backgroundColor: theme.palette.lynTheme.main,
											}}
										>
											Submit
										</Button>
									)}

									{viewData?.activationStatus !== 'W-I-P' && viewData?.activationStatus !== 'Closed' && (
										<Button
											style={{
												display: 'flex',
												marginLeft: '6px',
												alignItems: 'center',
												borderRadius: '4px',
											}}
											icon={<EditIcon width="18px" height="18px" color="#00914f" />}
											onClick={(e) => {
												e.stopPropagation();
												if (navigate && state) {
													navigate('/static/lease-record-management-v9/edit-new-version', {
														state: state,
													});
												}
											}}
										>
											Edit
										</Button>
									)}

									{areAllRecordsRecorded &&
										isLeaseLifeCycleOver() &&
										(viewData?.activationStatus !== 'Closed' ? (
											<Button
												style={{
													margin: '1px',
													display: 'flex',
													fontSize: '13px',
													fontWeight: '500',
													marginLeft: '6px',
													borderRadius: '6px',
													alignItems: 'center',
												}}
												icon={<AssignmentTurnedInOutlinedIcon />}
												onClick={(e) => {
													e.stopPropagation();
													handleSingleLeaseDeactivate('Closed');
												}}
											>
												Close
											</Button>
										) : (
											<Button
												style={{
													margin: '1px',
													display: 'flex',
													fontSize: '13px',
													marginLeft: '6px',
													fontWeight: '500',
													borderRadius: '6px',
													alignItems: 'center',
												}}
												icon={<RestorePageOutlinedIcon />}
												onClick={(e) => {
													e.stopPropagation();
													handleSingleLeaseDeactivate('Reopened');
												}}
											>
												Reopen
											</Button>
										))}
								</>
							)}
						</Box>
					</Box>

					{showClosedModal && (
						<ComptrollerConfirmModal
							modalData={modalData}
							showModal={showClosedModal}
							setShowModal={setShowClosedModal}
							handleSubmit={modalSubmitHandler}
						/>
					)}

					{formState?.showFormSubmitModal && (
						<ComptrollerConfirmModal
							handleSubmit={handleModalSubmit}
							modalData={formState?.modalData}
							setShowModal={setShowFormSubmitModal}
							showModal={formState?.showFormSubmitModal}
						/>
					)}
				</Box>
			)}
		</Box>
	);
};

export default LeaseDetailBasicInformation;
